<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ProjectLinkCard from '@/components/cards/ProjectLinkCard.vue';
import ProjectCardSkeleton from '@/components/cards/ProjectCardSkeleton.vue';
import LoadingSpinner from '@/components/utilities/LoadingSpinner.vue';
import { useProjectsApi } from '@/composables/useProjectsApi.ts';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls.ts';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls.ts';
import { ListingTypeEnum } from '@/enums/ListingType.ts';
import type { AvailableLocales, LocaleSchema } from '@/locales/index.ts';
import type { Project } from '@/typings/projects/Project.ts';

const { t, locale } = useI18n<{message: LocaleSchema}, AvailableLocales>({
  useScope: 'global',
});

const { getAllProjects, getAllProjectsLoading } = useProjectsApi();
const { getProjectWizardUrl: getRentalProjectUrl } = useRentalLocalizedUrls();
const { getProjectWizardUrl: getPurchaseProjectUrl } = usePurchaseLocalizedUrls();

const rentalProjects = ref<Project[]>([]);
const saleProjects = ref<Project[]>([]);
const selectedListingType = ref<ListingTypeEnum | null>(null);
const promotionsSectionRef = ref<HTMLElement | null>(null);

// Filtrer les projets actifs publiquement selon le type sélectionné
const filteredProjectsWithPromotion = computed(() => {
  if (!selectedListingType.value) return [];
  const projects = selectedListingType.value === ListingTypeEnum.Rental ? rentalProjects.value : saleProjects.value;
  return projects.filter(project => project.isPublicActive);
});

const getProjectUrl = computed(() => {
  if (!selectedListingType.value) return getRentalProjectUrl; // Fallback
  return selectedListingType.value === ListingTypeEnum.Rental ? getRentalProjectUrl : getPurchaseProjectUrl;
});

const isLoading = computed(() => {
  return getAllProjectsLoading.value;
});

const currentListingType = computed(() => {
  return selectedListingType.value as ListingTypeEnum;
});

function onListingTypeChange(listingType: ListingTypeEnum) {
  selectedListingType.value = listingType;
  
  // Attendre le prochain tick pour que le DOM soit mis à jour
  nextTick(() => {
    if (promotionsSectionRef.value) {
      promotionsSectionRef.value.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  });
}


onMounted(async () => {
  try {
    // Charger les projets en location avec promotions
    const [rentalProjectsData, saleProjectsData] = await Promise.all([
      getAllProjects(true, ListingTypeEnum.Rental),
      getAllProjects(true, ListingTypeEnum.Sale)
    ]);
    
    rentalProjects.value = rentalProjectsData;
    saleProjects.value = saleProjectsData;
  } catch (error) {
    console.error('Erreur lors du chargement des projets:', error);
    rentalProjects.value = [];
    saleProjects.value = [];
  }
});

</script>

<template>
  <!--Page Promotions-->
  <div class="promotions-browsing">
    <div class="wrapper wrapper_large">
      <section class="">
        <div class="section-header">
          <h2 class="">{{ t('browsingApp.promotions.chooseListingType') }}</h2>
        </div>
        <div class="promotions-selector">
          <button
            class="btn btn_big btn_textWhite"
            :class="{ 'btn_selected': selectedListingType === ListingTypeEnum.Sale }"
            @click="onListingTypeChange(ListingTypeEnum.Sale)"
            type="button"
          >
            {{ t('browsingApp.listingTypes.sale') }}
          </button>
          <button
            class="btn btn_big btn_textWhite"
            :class="{ 'btn_selected': selectedListingType === ListingTypeEnum.Rental }"
            @click="onListingTypeChange(ListingTypeEnum.Rental)"
            type="button"
          >
            {{ t('browsingApp.listingTypes.rental') }}
          </button>
        </div>
      </section>
      <section>
        <!-- Loading skeleton -->
        <ProjectCardSkeleton v-if="isLoading" />
        <!-- Content based on loading and data states -->
        <div v-else-if="selectedListingType && filteredProjectsWithPromotion.length === 0"
             class="promotionsBrowsing-noProjects">
          <h3>{{ t('browsingApp.promotions.noPromotionsAvailable') }}</h3>
        </div>
        <div v-else-if="selectedListingType" 
             ref="promotionsSectionRef"
             class="promotionsBrowsing-section">
          <div>
            <h3>{{ selectedListingType === ListingTypeEnum.Rental ? t('browsingApp.promotions.rentalPromotions') : t('browsingApp.promotions.salePromotions') }}</h3>
          </div>
          <div class="promotionsBrowsing-projectsGrid preferredColumnCountGrid">
            <ProjectLinkCard v-for="project in filteredProjectsWithPromotion"
                             :key="project.id"
                             :project="project"
                             :href="getProjectUrl(project)"
                             :h4-title="false"
                             :listing-type="currentListingType" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.promotions-browsing{
  padding:clamp(6rem, 3.906vw + 4.5rem, 12rem) 0;

  .section-header{
    text-align:center;

    h2{
      font-size: clamp(3.4rem, 1.991vw + 2.205rem, 5.2rem);
      margin:0;
    }
  }
  .promotions-selector{
    display: flex;
    justify-content: center;
    gap:2.0rem;
    padding:clamp(3rem, 3.356vw - 0.443rem, 6rem) 0;

    .btn{
      width: 100%;
      max-width: 300px;
    }
  }
  .promotionsBrowsing-section{
    border-top: 1px solid var(--gray-200);
    padding: var(--spc48-24) 0 0 0;

    h3{
      margin-bottom: var(--spc48-24);
      font-family: Inter;
      font-size: var(--fs34-22);
      line-height: 1.125;
      text-align:left;
    }
  }
}
:deep(){
  .tagPromotion {
    display: inline-flex;
    align-items: center;
    padding: 8px 12px;
    border-radius: 3rem;
    background-color: oklch(45% 0.16 290deg);
    color: #ffffff;
    font-size: 0.9em;
    font-weight: 500;
    line-height: 1.125;
  }
}

</style>